import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    Controller,
    useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import BusinessIcon from "@mui/icons-material/Business";
import BadgeIcon from "@mui/icons-material/Badge";
import DescriptionIcon from "@mui/icons-material/Description";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AirlineSeatReclineNormalIcon from "@mui/icons-material/AirlineSeatReclineNormal";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SpeedIcon from "@mui/icons-material/Speed";
import CategoryIcon from "@mui/icons-material/Category";

import { useServicesTypesProject } from "../../hooks/administration/useServicesTypesProject";

// ============================================================
// CONSTANTES Y HELPERS
// ============================================================
const OWNERSHIP_OPTIONS = [
    { value: 'OWNED', label: 'Propio (de la empresa)', color: 'success' },
    { value: 'CUSTOMER', label: 'De cliente (bajo administración)', color: 'info' },
];

const VEHICLE_STATUS = { ACTIVO: "ACTIVO", INACTIVO: "INACTIVO" };
const VEHICLE_STATUS_OPTIONS = [
    { value: VEHICLE_STATUS.ACTIVO, label: "ACTIVO", color: "success" },
    { value: VEHICLE_STATUS.INACTIVO, label: "INACTIVO", color: "warning" },
];

const currentYear = new Date().getFullYear();

const getOrganizationId = (org) => Number(org?.orgid ?? org?.OrgID);
const getOrganizationName = (org) => org?.orgnom ?? org?.OrgNom ?? "Organización";
const getOrganizationType = (org) => org?.orgtip ?? org?.OrgTip ?? "";
const getSerProId = (item) => {
    const id = Number(item?.serproid ?? item?.SerProID ?? item?.serProId ?? item?.SerProId);
    return Number.isInteger(id) && id > 0 ? id : null;
};
const getProNom = (item) => item?.pronom ?? item?.ProNom ?? "-";
const getSerProNom = (item) => item?.serpronom ?? item?.SerProNom ?? "-";
const getTipSerNom = (item) => item?.tipsernom ?? item?.TipSerNom ?? "-";
const getRolVehPro = (item) => item?.rolvehpro ?? item?.RolVehPro ?? item?.rolVehPro ?? "";

const normalizeDefaultServices = (services = []) => {
    if (!Array.isArray(services)) return [];
    const normalized = services.map((service) => {
        const serProId = getSerProId(service);
        if (!serProId) return null;
        return { serProId, rolVehPro: getRolVehPro(service)?.trim() || null };
    }).filter(Boolean);

    return Array.from(new Map(normalized.map(item => [item.serProId, item])).values());
};

// ============================================================
// ESQUEMA DE VALIDACIÓN
// ============================================================
const vehicleSchema = yup.object({
    orgid: yup.number().typeError("Debe seleccionar una empresa").required("Empresa requerida"),
    vehmat: yup.string().trim().required("Matrícula obligatoria").max(20, "Máximo 20 caracteres"),
    vehdes: yup.string().transform((v) => v?.trim() === "" ? null : v).nullable().max(255, "Máximo 255 caracteres"),
    vehmar: yup.string().trim().required("Marca obligatoria").max(100, "Máximo 100 caracteres"),
    vehmod: yup.string().trim().required("Modelo obligatorio").max(100, "Máximo 100 caracteres"),
    vehani: yup.number().transform((val, orig) => orig === "" || orig === null ? null : val).nullable()
        .typeError("Año numérico").integer("Entero").min(1900, "Mínimo 1900").max(currentYear + 1, `Máximo ${currentYear + 1}`),
    vehcap: yup.number().transform((val, orig) => orig === "" || orig === null ? null : val).nullable()
        .typeError("Capacidad numérica").integer("Entero").positive("Mayor a cero").max(200, "Máximo 200"),
    vehkmactual: yup.number().transform((val, orig) => orig === "" || orig === null ? null : val).nullable()
        .typeError("Km debe ser numérico").min(0, "No puede ser negativo"),
    vehest: yup.string().oneOf(Object.values(VEHICLE_STATUS), "Estado inválido").required("Estado obligatorio"),
    ownershiptype: yup.string().oneOf(['OWNED', 'CUSTOMER'], 'Tipo inválido').required('Requerido'),
    serviciosProyecto: yup.array().of(
        yup.object({
            serProId: yup.number().integer().positive().required(),
            rolVehPro: yup.string().nullable().max(100, "Máximo 100 caracteres"),
        })
    ).default([]),
});

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
const VehicleForm = ({
    open,
    handleClose,
    onSubmit,
    defaultValues = {},
    organizations = [],
    isSubmitting = false,
}) => {
    const isEditing = Boolean(defaultValues?.vehid);
    const [assignedServicesData, setAssignedServicesData] = useState([]);
    const [serviceSearch, setServiceSearch] = useState("");

    const { servicesTypesProject = [], isLoading: loadingServices } = useServicesTypesProject();

    const {
        control, register, handleSubmit, reset, setValue,
        formState: { errors, isSubmitting: isFormSubmitting },
    } = useForm({
        resolver: yupResolver(vehicleSchema),
        defaultValues: {
            orgid: "",
            vehmat: "",
            vehdes: "",
            vehmar: "",
            vehmod: "",
            vehani: "",
            vehcap: "",
            vehkmactual: "",
            vehest: VEHICLE_STATUS.ACTIVO,
            ownershiptype: 'OWNED',
            serviciosProyecto: [],
        },
    });

    const defaultServices = useMemo(() => {
        const services = defaultValues?.serviciosProyecto ?? defaultValues?.ServiciosProyecto ?? [];
        return normalizeDefaultServices(services).sort((a, b) => a.serProId - b.serProId);
    }, [defaultValues?.serviciosProyecto, defaultValues?.ServiciosProyecto]);

    const servicesOptions = useMemo(() => Array.isArray(servicesTypesProject) ? servicesTypesProject : [], [servicesTypesProject]);
    const assignedServiceIds = useMemo(() => assignedServicesData.map((item) => item.serProId), [assignedServicesData]);

    const assignedServices = useMemo(() => {
        const assignedSet = new Set(assignedServiceIds);
        return servicesOptions.filter((service) => assignedSet.has(getSerProId(service)));
    }, [servicesOptions, assignedServiceIds]);

    const filteredAvailableServices = useMemo(() => {
        const assignedSet = new Set(assignedServiceIds);
        const available = servicesOptions.filter((service) => !assignedSet.has(getSerProId(service)));
        const term = serviceSearch.trim().toLowerCase();
        if (!term) return available;
        return available.filter((service) =>
            [getSerProNom(service), getProNom(service), getTipSerNom(service)].some((field) =>
                String(field).toLowerCase().includes(term)
            )
        );
    }, [servicesOptions, assignedServiceIds, serviceSearch]);

    // RESET AL ABRIR
    useEffect(() => {
        if (!open) return;
        const defaultOrgId = defaultValues?.orgid ?? organizations?.[0]?.orgid ?? "";

        reset({
            orgid: defaultOrgId ? String(defaultOrgId) : "",
            vehmat: defaultValues?.vehmat ?? "",
            vehdes: defaultValues?.vehdes ?? "",
            vehmar: defaultValues?.vehmar ?? "",
            vehmod: defaultValues?.vehmod ?? "",
            vehani: defaultValues?.vehani ?? "",
            vehcap: defaultValues?.vehcap ?? "",
            vehkmactual: defaultValues?.vehkmactual ?? defaultValues?.VehKmActual ?? "",
            vehest: defaultValues?.vehest ?? VEHICLE_STATUS.ACTIVO,
            ownershiptype: defaultValues?.ownershiptype ?? defaultValues?.OwnershipType ?? 'OWNED',
            serviciosProyecto: defaultServices,
        });

        setAssignedServicesData(defaultServices);
        setServiceSearch("");
    }, [open, reset, defaultValues, defaultServices, organizations]);

    useEffect(() => {
        setValue("serviciosProyecto", assignedServicesData, { shouldValidate: true, shouldDirty: true });
    }, [assignedServicesData, setValue]);

    // HANDLERS
    const handleFormClose = useCallback(() => {
        if (isSubmitting || isFormSubmitting) return;
        reset();
        setAssignedServicesData([]);
        setServiceSearch("");
        handleClose();
    }, [reset, handleClose, isSubmitting, isFormSubmitting]);

    const handleAddService = useCallback((service) => {
        const serProId = getSerProId(service);
        if (!serProId) return;
        setAssignedServicesData((current) => {
            if (current.some((item) => item.serProId === serProId)) return current;
            return [...current, { serProId, rolVehPro: "VEHICULO" }];
        });
    }, []);

    const handleRemoveService = useCallback((serProId) => {
        const id = Number(serProId);
        if (!Number.isInteger(id)) return;
        setAssignedServicesData((current) => current.filter((item) => item.serProId !== id));
    }, []);

    const handleServiceRoleChange = useCallback((serProId, role) => {
        const id = Number(serProId);
        if (!Number.isInteger(id)) return;
        setAssignedServicesData((current) =>
            current.map((item) => item.serProId === id ? { ...item, rolVehPro: role || null } : item)
        );
    }, []);

    const handleFormSubmit = useCallback((data) => {
        const payload = {
            orgid: Number(data.orgid),
            vehmat: data.vehmat.trim().toUpperCase(),
            vehdes: data.vehdes?.trim() || null,
            vehmar: data.vehmar.trim(),
            vehmod: data.vehmod.trim(),
            vehani: data.vehani === "" || data.vehani === null ? null : Number(data.vehani),
            vehcap: data.vehcap === "" || data.vehcap === null ? null : Number(data.vehcap),
            vehkmactual: data.vehkmactual === "" || data.vehkmactual === null ? null : Number(data.vehkmactual),
            vehest: data.vehest,
            ownershiptype: data.ownershiptype,
            serviciosProyecto: assignedServicesData.map((a) => ({
                serProId: Number(a.serProId),
                rolVehPro: a.rolVehPro?.trim() || null,
            })),
        };
        onSubmit(payload);
    }, [onSubmit, assignedServicesData]);

    const submitting = isSubmitting || isFormSubmitting;

    // RENDER
    return (
        <Dialog open={open} onClose={handleFormClose} fullWidth maxWidth="md" disableEscapeKeyDown={submitting}>
            <DialogTitle sx={{ bgcolor: "primary.main", color: "white", display: "flex", alignItems: "center", gap: 2 }}>
                <DirectionsCarIcon />
                {isEditing ? "Editar vehículo" : "Crear nuevo vehículo"}
            </DialogTitle>

            <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
                <DialogContent dividers>
                    <Grid container spacing={4}>
                        {/* SECCIÓN 1: IDENTIFICACIÓN */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <DirectionsCarIcon color="primary" /> Identificación
                            </Typography>

                            <Controller
                                name="orgid"
                                control={control}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <Autocomplete
                                        options={organizations}
                                        getOptionLabel={(option) => `${getOrganizationName(option)} ${getOrganizationType(option) ? `(${getOrganizationType(option)})` : ""}`}
                                        value={organizations.find((org) => String(getOrganizationId(org)) === value) || null}
                                        onChange={(_, newValue) => onChange(newValue ? String(getOrganizationId(newValue)) : "")}
                                        disabled={submitting}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Empresa proveedora"
                                                margin="normal"
                                                error={Boolean(error)}
                                                helperText={error?.message}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <InputAdornment position="start"><BusinessIcon color={error ? "error" : "action"} /></InputAdornment>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />

                            <TextField
                                label="Matrícula o placa" fullWidth margin="normal" disabled={submitting}
                                inputProps={{ maxLength: 20 }}
                                {...register("vehmat")}
                                error={Boolean(errors.vehmat)} helperText={errors.vehmat?.message}
                                InputProps={{ startAdornment: <InputAdornment position="start"><BadgeIcon color={errors.vehmat ? "error" : "action"} /></InputAdornment> }}
                            />

                            <TextField
                                label="Marca" fullWidth margin="normal" disabled={submitting} inputProps={{ maxLength: 100 }}
                                {...register("vehmar")}
                                error={Boolean(errors.vehmar)} helperText={errors.vehmar?.message}
                            />

                            <TextField
                                label="Modelo" fullWidth margin="normal" disabled={submitting} inputProps={{ maxLength: 100 }}
                                {...register("vehmod")}
                                error={Boolean(errors.vehmod)} helperText={errors.vehmod?.message}
                            />

                            <TextField
                                label="Descripción" fullWidth multiline minRows={2} margin="normal" disabled={submitting} inputProps={{ maxLength: 255 }}
                                {...register("vehdes")}
                                error={Boolean(errors.vehdes)} helperText={errors.vehdes?.message}
                            />
                        </Grid>

                        {/* SECCIÓN 2: OPERACIÓN */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <AssignmentIcon color="primary" /> Operación
                            </Typography>

                            <TextField
                                label="Año" type="number" fullWidth margin="normal" disabled={submitting} inputProps={{ min: 1900, max: currentYear + 1, step: 1 }}
                                {...register("vehani")}
                                error={Boolean(errors.vehani)} helperText={errors.vehani?.message}
                                InputProps={{ startAdornment: <InputAdornment position="start"><CalendarMonthIcon color={errors.vehani ? "error" : "action"} /></InputAdornment> }}
                            />

                            <TextField
                                label="Capacidad de pasajeros" type="number" fullWidth margin="normal" disabled={submitting} inputProps={{ min: 1, max: 200, step: 1 }}
                                {...register("vehcap")}
                                error={Boolean(errors.vehcap)} helperText={errors.vehcap?.message}
                                InputProps={{ startAdornment: <InputAdornment position="start"><AirlineSeatReclineNormalIcon color={errors.vehcap ? "error" : "action"} /></InputAdornment> }}
                            />

                            <TextField
                                label="Kilometraje actual" type="number" fullWidth margin="normal" disabled={submitting} inputProps={{ step: 0.1 }}
                                {...register("vehkmactual")}
                                error={Boolean(errors.vehkmactual)} helperText={errors.vehkmactual?.message || "Último km conocido"}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SpeedIcon color={errors.vehkmactual ? "error" : "action"} /></InputAdornment> }}
                            />

                            <Controller
                                name="ownershiptype"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field} select label="Tipo de tenencia" fullWidth margin="normal" disabled={submitting}
                                        error={Boolean(error)} helperText={error?.message}
                                        InputProps={{ startAdornment: <InputAdornment position="start"><CategoryIcon color={error ? "error" : "action"} /></InputAdornment> }}
                                    >
                                        {OWNERSHIP_OPTIONS.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                <Chip label={opt.label} color={opt.color} size="small" />
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />

                            <Controller
                                name="vehest"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field} select label="Estado operativo" fullWidth margin="normal" disabled={submitting}
                                        error={Boolean(error)} helperText={error?.message}
                                        InputProps={{ startAdornment: <InputAdornment position="start"><ToggleOnIcon /></InputAdornment> }}
                                    >
                                        {VEHICLE_STATUS_OPTIONS.map((status) => (
                                            <MenuItem key={status.value} value={status.value}>
                                                <Chip label={status.label} color={status.color} size="small" />
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        {/* SECCIÓN 3: SERVICIOS */}
                        <Grid item xs={12}>
                            <Divider sx={{ mb: 3 }} />
                            <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <AssignmentIcon color="primary" /> Servicios-proyecto
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Seleccione los servicios en los que este vehículo puede operar.
                            </Typography>

                            <Grid container spacing={2} mt={1}>
                                {/* DISPONIBLES */}
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
                                        <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <AddCircleIcon color="primary" /> Disponibles
                                        </Typography>
                                        <Divider sx={{ my: 1 }} />
                                        <TextField
                                            size="small" fullWidth label="Buscar servicio..." value={serviceSearch} disabled={submitting}
                                            onChange={(e) => setServiceSearch(e.target.value)} sx={{ mb: 2 }}
                                        />
                                        {loadingServices ? (
                                            <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}><CircularProgress size={28} /></Box>
                                        ) : filteredAvailableServices.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                {serviceSearch.trim() ? "No se encontraron servicios" : "No hay servicios disponibles"}
                                            </Typography>
                                        ) : (
                                            <Stack spacing={1} sx={{ maxHeight: 300, overflowY: 'auto' }}>
                                                {filteredAvailableServices.map((service) => (
                                                    <Paper key={getSerProId(service)} variant="outlined" sx={{ p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={600}>{getSerProNom(service)}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{getProNom(service)} — {getTipSerNom(service)}</Typography>
                                                        </Box>
                                                        <IconButton type="button" color="primary" size="small" disabled={submitting} onClick={() => handleAddService(service)}>
                                                            <AddCircleIcon fontSize="small" />
                                                        </IconButton>
                                                    </Paper>
                                                ))}
                                            </Stack>
                                        )}
                                    </Paper>
                                </Grid>

                                {/* ASIGNADOS */}
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
                                        <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <RemoveCircleIcon color="primary" /> Asignados
                                        </Typography>
                                        <Divider sx={{ my: 1 }} />
                                        {assignedServices.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">No hay servicios asignados</Typography>
                                        ) : (
                                            <Stack spacing={1} sx={{ maxHeight: 300, overflowY: 'auto' }}>
                                                {assignedServices.map((service) => {
                                                    const serviceId = getSerProId(service);
                                                    const assignment = assignedServicesData.find((item) => item.serProId === serviceId);
                                                    return (
                                                        <Paper key={serviceId} variant="outlined" sx={{ p: 1.5 }}>
                                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                    <Typography variant="body2" fontWeight={600}>{getSerProNom(service)}</Typography>
                                                                    <Typography variant="caption" color="text.secondary">{getProNom(service)} — {getTipSerNom(service)}</Typography>
                                                                    <TextField
                                                                        size="small" fullWidth label="Función (Ej. TAXI, BUS)" placeholder="Ej. TAXI"
                                                                        inputProps={{ maxLength: 100 }} disabled={submitting} value={assignment?.rolVehPro ?? ""}
                                                                        onChange={(e) => handleServiceRoleChange(serviceId, e.target.value)} sx={{ mt: 1.5 }}
                                                                    />
                                                                </Box>
                                                                <IconButton type="button" color="error" size="small" disabled={submitting} onClick={() => handleRemoveService(serviceId)}>
                                                                    <RemoveCircleIcon fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        </Paper>
                                                    );
                                                })}
                                            </Stack>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 2, bgcolor: "grey.100" }}>
                    <Button type="button" onClick={handleFormClose} variant="outlined" color="inherit" disabled={submitting}>
                        Cancelar
                    </Button>
                    <Button type="submit" variant="contained" color="primary" disabled={submitting} startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}>
                        {submitting ? "Guardando..." : isEditing ? "Actualizar" : "Guardar"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default VehicleForm;